import { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import {
	Trophy,
	Medal,
	Star,
	ChevronUp,
	TrendingUp,
	Lightbulb,
	Users,
	ArrowLeft,
} from "lucide-react";
import type { Idea, User } from "@/lib/types";

interface RankingsProps {
	ideas: Idea[];
	users: User[];
	onSelectIdea: (ideaId: string) => void;
	onBack: () => void;
}

function getRankIcon(index: number) {
	switch (index) {
		case 0:
			return <Trophy className="w-5 h-5 text-yellow-500" />;
		case 1:
			return <Medal className="w-5 h-5 text-gray-400" />;
		case 2:
			return <Medal className="w-5 h-5 text-amber-700" />;
		default:
			return (
				<span className="w-5 h-5 flex items-center justify-center text-sm font-bold text-muted-foreground">
					{index + 1}
				</span>
			);
	}
}

export function Rankings({
	ideas,
	users,
	onSelectIdea,
	onBack,
}: RankingsProps) {
	const weeklyIdeas = useMemo(() => {
		const oneWeekAgo = new Date();
		oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
		return ideas
			.filter((i) => new Date(i.createdAt) >= oneWeekAgo)
			.sort((a, b) => b.votes - a.votes)
			.slice(0, 10);
	}, [ideas]);

	const allTimeIdeas = useMemo(
		() => [...ideas].sort((a, b) => b.votes - a.votes).slice(0, 10),
		[ideas],
	);

	const topCreators = useMemo(
		() => [...users].sort((a, b) => b.reputation - a.reputation).slice(0, 10),
		[users],
	);

	const userMap = useMemo(() => {
		const m = new Map<string, User>();
		for (const u of users) m.set(u.id, u);
		return m;
	}, [users]);

	return (
		<div className="space-y-8">
			<button
				onClick={onBack}
				className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
			>
				<ArrowLeft className="w-4 h-4" />
				Back to feed
			</button>

			<div>
				<h2 className="text-2xl font-bold flex items-center gap-2">
					<Trophy className="w-6 h-6 text-yellow-500" />
					Rankings
				</h2>
				<p className="text-sm text-muted-foreground mt-1">
					Top ideas and creators across the community
				</p>
			</div>

			<div className="grid gap-8 lg:grid-cols-2">
				{/* Weekly top ideas */}
				<div className="rounded-xl border border-border bg-card p-5 space-y-4">
					<div className="flex items-center gap-2">
						<TrendingUp className="w-5 h-5 text-orange-500" />
						<h3 className="font-semibold">This Week's Top Ideas</h3>
					</div>
					<Separator />
					<div className="space-y-2">
						{weeklyIdeas.map((idea, index) => {
							const ideaAuthor = userMap.get(idea.authorId);
							return (
								<button
									key={idea.id}
									onClick={() => onSelectIdea(idea.id)}
									className="w-full text-left flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
								>
									{getRankIcon(index)}
									<div className="flex-1 min-w-0">
										<p className="text-sm font-medium truncate">
											{idea.title}
										</p>
										<p className="text-xs text-muted-foreground">
											by {ideaAuthor?.name ?? "Unknown"}
										</p>
									</div>
									<div className="flex items-center gap-1 text-sm font-bold text-orange-500 shrink-0">
										<ChevronUp className="w-4 h-4" />
										{idea.votes}
									</div>
								</button>
							);
						})}
						{weeklyIdeas.length === 0 && (
							<p className="text-sm text-muted-foreground text-center py-6">
								No ideas submitted this week yet
							</p>
						)}
					</div>
				</div>

				{/* All time top ideas */}
				<div className="rounded-xl border border-border bg-card p-5 space-y-4">
					<div className="flex items-center gap-2">
						<Lightbulb className="w-5 h-5 text-yellow-500" />
						<h3 className="font-semibold">All-Time Top Ideas</h3>
					</div>
					<Separator />
					<div className="space-y-2">
						{allTimeIdeas.map((idea, index) => {
							const ideaAuthor = userMap.get(idea.authorId);
							return (
								<button
									key={idea.id}
									onClick={() => onSelectIdea(idea.id)}
									className="w-full text-left flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
								>
									{getRankIcon(index)}
									<div className="flex-1 min-w-0">
										<p className="text-sm font-medium truncate">
											{idea.title}
										</p>
										<p className="text-xs text-muted-foreground">
											by {ideaAuthor?.name ?? "Unknown"}
										</p>
									</div>
									<div className="flex items-center gap-1 text-sm font-bold text-orange-500 shrink-0">
										<ChevronUp className="w-4 h-4" />
										{idea.votes}
									</div>
								</button>
							);
						})}
					</div>
				</div>

				{/* Top creators */}
				<div className="rounded-xl border border-border bg-card p-5 space-y-4 lg:col-span-2">
					<div className="flex items-center gap-2">
						<Users className="w-5 h-5 text-purple-500" />
						<h3 className="font-semibold">Top Creators</h3>
					</div>
					<Separator />
					<div className="grid gap-2 sm:grid-cols-2">
						{topCreators.map((user, index) => (
							<div
								key={user.id}
								className="flex items-center gap-3 p-3 rounded-lg bg-muted/30"
							>
								{getRankIcon(index)}
								<Avatar className="w-9 h-9">
									<AvatarFallback className="text-xs bg-primary text-primary-foreground">
										{user.avatar}
									</AvatarFallback>
								</Avatar>
								<div className="flex-1 min-w-0">
									<p className="text-sm font-medium">{user.name}</p>
									<p className="text-xs text-muted-foreground">
										{user.ideasSubmitted} ideas &middot;{" "}
										{user.totalVotesReceived} votes received
									</p>
								</div>
								<Badge
									variant="secondary"
									className="shrink-0 gap-1"
								>
									<Star className="w-3 h-3 text-amber-500" />
									{user.reputation}
								</Badge>
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	);
}
